import React, { createContext, useContext, useState, useEffect } from 'react';

const PlanificationContext = createContext();

// Clé pour sessionStorage
const STORAGE_KEY = 'planification_data';
const STORAGE_PARAMS_KEY = 'planification_params';

export const PlanificationProvider = ({ children }) => {
  const [planificationData, setPlanificationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastParams, setLastParams] = useState(null);

  // Charger les données depuis sessionStorage au montage
  useEffect(() => {
    try {
      const savedData = sessionStorage.getItem(STORAGE_KEY);
      const savedParams = sessionStorage.getItem(STORAGE_PARAMS_KEY);
      
      if (savedData && savedParams) {
        setPlanificationData(JSON.parse(savedData));
        setLastParams(JSON.parse(savedParams));
        console.log('✅ Données chargées depuis sessionStorage');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement depuis sessionStorage:', error);
      // Si erreur, nettoyer le storage
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_PARAMS_KEY);
    }
  }, []);

  // Sauvegarder les données dans sessionStorage
  const saveToStorage = (data, params) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      sessionStorage.setItem(STORAGE_PARAMS_KEY, JSON.stringify(params));
      console.log('✅ Données sauvegardées dans sessionStorage');
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde dans sessionStorage:', error);
      // Si quota dépassé, essayer de nettoyer
      if (error.name === 'QuotaExceededError') {
        console.warn('⚠️ Quota sessionStorage dépassé, nettoyage...');
        sessionStorage.clear();
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
          sessionStorage.setItem(STORAGE_PARAMS_KEY, JSON.stringify(params));
        } catch (retryError) {
          console.error('❌ Impossible de sauvegarder même après nettoyage');
        }
      }
    }
  };

  // Mettre à jour les données
  const updatePlanificationData = (data, params) => {
    setPlanificationData(data);
    setLastParams(params);
    saveToStorage(data, params);
  };

  // Vérifier si les données sont déjà chargées pour ces paramètres
  const hasDataForParams = (params) => {
    if (!planificationData || !lastParams) return false;
    
    return (
      lastParams.dateDebut === params.dateDebut &&
      lastParams.dateFin === params.dateFin
    );
  };

  // Nettoyer les données (déconnexion, changement de période, etc.)
  const clearData = () => {
    setPlanificationData(null);
    setLastParams(null);
    setError(null);
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_PARAMS_KEY);
    console.log('🗑️ Données nettoyées');
  };

  const value = {
    planificationData,
    isLoading,
    error,
    lastParams,
    updatePlanificationData,
    hasDataForParams,
    clearData,
    setIsLoading,
    setError,
  };

  return (
    <PlanificationContext.Provider value={value}>
      {children}
    </PlanificationContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const usePlanification = () => {
  const context = useContext(PlanificationContext);
  if (!context) {
    throw new Error('usePlanification doit être utilisé dans un PlanificationProvider');
  }
  return context;
};
